import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

// One-shot destructive wipe of trial sales data. Admin-only (the backend
// enforces this). Requires typing RESET to confirm — guards against an
// accidental click that would nuke real sales after launch.
export default function ResetSalesDialog({ open, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [opts, setOpts] = useState({
    online_orders: true,
    pos_sales: false,
    shifts: false,
    loyalty_points: false,
  });

  const setOpt = (k) => (v) => setOpts((s) => ({ ...s, [k]: !!v }));

  const submit = async () => {
    if (confirm !== "RESET") { toast.error("Type RESET (uppercase) to confirm"); return; }
    if (!Object.values(opts).some(Boolean)) { toast.error("Pick at least one thing to wipe"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/reset-sales", { confirm, ...opts });
      const parts = [];
      if (data.online_orders_deleted != null) parts.push(`${data.online_orders_deleted} online orders`);
      if (data.pos_sales_deleted != null) parts.push(`${data.pos_sales_deleted} POS sales`);
      if (data.shifts_deleted != null) parts.push(`${data.shifts_deleted} shifts`);
      if (data.loyalty_points_reset != null) parts.push(`${data.loyalty_points_reset} customer point balances`);
      toast.success(`Wiped: ${parts.join(" · ") || "nothing"}`, { duration: 6000 });
      setConfirm("");
      if (onDone) onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-md" data-testid="reset-sales-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#C62828]">
            <AlertTriangle className="h-6 w-6" />
            Reset Sales Data
          </DialogTitle>
        </DialogHeader>

        <div className="bg-[#FDEAEA] border border-[#C62828] rounded-lg p-3 text-xs text-[#7A1F1F]">
          <b>This deletes records and cannot be undone.</b> Use this to wipe trial / test data before going live.
          Products, staff accounts, and settings are <b>not</b> touched.
        </div>

        <div className="space-y-3 pt-2">
          <Label className="font-bold text-sm text-[#1C2826]">What to wipe</Label>
          {[
            ["online_orders",  "All online customer orders",  "Every cart checkout from the storefront (delivery + pickup)."],
            ["pos_sales",      "All POS sales",                "Every walk-in sale recorded by cashiers at the till."],
            ["shifts",         "All cashier shifts",          "Opening / closing-cash records. Cashiers will start fresh."],
            ["loyalty_points", "Reset all Suki points to 0",  "Every customer's loyalty balance resets to 0."],
          ].map(([key, title, hint]) => (
            <label key={key} className="flex items-start gap-2 cursor-pointer select-none" data-testid={`reset-opt-${key}`}>
              <Checkbox checked={opts[key]} onCheckedChange={setOpt(key)} className="mt-1" />
              <span className="flex-1 text-sm">
                <span className="font-semibold text-[#1C2826]">{title}</span>
                <span className="block text-xs text-[#4A5D5A]">{hint}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-1 pt-3 border-t border-[#E1E5DF]">
          <Label htmlFor="reset-confirm" className="text-xs">Type <b className="text-[#C62828]">RESET</b> to confirm</Label>
          <Input
            id="reset-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET"
            className="h-11 font-mono"
            autoComplete="off"
            data-testid="reset-confirm-input"
          />
        </div>

        <div className="pt-3 border-t border-[#E1E5DF] grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="reset-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || confirm !== "RESET"}
            className="bg-[#C62828] hover:bg-[#8E1F1F] text-white font-bold"
            data-testid="reset-confirm-btn"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Wiping…</> : <><Trash2 className="h-4 w-4 mr-1" /> Wipe data</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
